/* oxlint-disable no-void */
import { useEffect, useMemo, useRef, useState } from 'react';
import { di } from 'react-magnetic-di';
import { useInlineFields } from '..';
import {
	FORM_SUBMISSION_PROCEED,
	FORM_SUBMISSION_RE_SUBMIT_AND_PROCEED,
	FORM_SUBMISSION_TIMEOUT,
} from '../../../constants';
import { type ForgeCustomFieldsResolver } from '../../../types';
import { FIELD_STATE_PENDING, FIELD_STATE_FOCUSED, FIELD_STATE_TIMED_OUT } from '../constants';
import type { InlineFieldState } from '../types';

export const useForgeCustomFieldsResolver = (resolver?: ForgeCustomFieldsResolver) => {
	di(useInlineFields);

	const [fieldsState, { setFieldState }] = useInlineFields();
	const fieldsStateRef = useRef(fieldsState);
	const timeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>();

	const [shouldResolveWhenSettled, setShouldResolveWhenSettled] = useState(false);

	const areFieldsReadyToBeSubmitted = useMemo(
		() => areAllResolved(Object.values(fieldsState)),
		[fieldsState],
	);

	useEffect(() => {
		fieldsStateRef.current = fieldsState;
	}, [fieldsState]);

	useEffect(() => {
		if (shouldResolveWhenSettled && areFieldsReadyToBeSubmitted) {
			clearTimeout(timeoutRef.current);

			resolver?.resolve(FORM_SUBMISSION_RE_SUBMIT_AND_PROCEED);
		}
	}, [shouldResolveWhenSettled, areFieldsReadyToBeSubmitted, resolver]);

	useEffect(
		() => {
			if (!resolver) return undefined;

			// No Forge custom fields to wait for (well, there might be some, but they are all already resolved).
			if (areAllResolved(Object.values(fieldsStateRef.current))) {
				resolver.resolve(FORM_SUBMISSION_PROCEED);
				return undefined;
			}

			// Marking that we should resolve once the fields are settled.
			setShouldResolveWhenSettled(true);

			// Let all the fields with a form submission handler to submit their values.
			Object.values(fieldsStateRef.current).forEach(({ onFormSubmit }) => onFormSubmit?.());

			// It might be the case that some field goes crazy and does not submit its value.
			// In this case we will resolve the promise after the timeout to unblock the request creation.
			clearTimeout(timeoutRef.current);
			timeoutRef.current = setTimeout(() => {
				Object.entries(fieldsStateRef.current).forEach(([fieldId, fieldState]) => {
					if (fieldState.status === FIELD_STATE_PENDING) {
						void setFieldState({
							fieldId,
							status: FIELD_STATE_TIMED_OUT,
						});
					}
				});
				resolver?.resolve(FORM_SUBMISSION_RE_SUBMIT_AND_PROCEED);
			}, FORM_SUBMISSION_TIMEOUT);

			return () => {
				resolver?.reject();
				setShouldResolveWhenSettled(false);
				clearTimeout(timeoutRef.current);
			};
		},
		// The only dependency that could be added here is the resolver.
		// We do not want the timeout to randomly restart.
		// If you need to add more dependencies, please use refs.
		// oxlint-disable-next-line react-hooks/exhaustive-deps
		[resolver],
	);
};
const UNRESOLVED_STATES = [FIELD_STATE_PENDING, FIELD_STATE_FOCUSED];
const areAllResolved = (fieldsState: InlineFieldState[]) => {
	return fieldsState.every(({ status }) => !UNRESOLVED_STATES.includes(status));
};
